import { useState, useEffect, useRef } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { useIsMobile } from '../hooks/useIsMobile';

const LINKS = [
  { label: 'About', id: 'about' },
  { label: 'Speaking', id: 'speaking' },
  { label: 'Training', id: 'training' },
  { label: 'Assessments', id: 'assessments' },
  { label: 'FAQ', id: 'faq' },
  { label: 'Contact', id: 'contact' },
];

const VENMO_HANDLE = 'Tracy-Curtis-35';
const VENMO_URL = `https://venmo.com/u/${VENMO_HANDLE}`;
const ZELLE_NUMBER = '+1 (219) 671-4456';

const SECTIONS = ['hero', 'about', 'speaking', 'speaker-kit', 'training', 'assessments', 'approach', 'testimonials', 'faq', 'lead', 'contact'];

interface NavProps { onNav: (id: string) => void }

export function Nav({ onNav }: NavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('hero');
  const [menuOpen, setMenuOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payMobileOpen, setPayMobileOpen] = useState(false);
  const [zelleCopied, setZelleCopied] = useState(false);
  const payRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const h = () => {
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i]);
        if (el && el.getBoundingClientRect().top <= 200) { setActive(SECTIONS[i]); break; }
      }
    };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close drawer on resize to desktop
  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);

  // Click-outside to close desktop Pay dropdown
  useEffect(() => {
    if (!payOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (payRef.current && !payRef.current.contains(e.target as Node)) setPayOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [payOpen]);

  const go = (id: string) => { setMenuOpen(false); setTimeout(() => onNav(id), 120); };

  const copyZelle = async () => {
    try {
      await navigator.clipboard.writeText(ZELLE_NUMBER);
      setZelleCopied(true);
      setTimeout(() => setZelleCopied(false), 1800);
    } catch { /* noop */ }
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 150,
        background: scrolled || menuOpen ? 'rgba(13,8,20,0.97)' : 'transparent',
        backdropFilter: scrolled || menuOpen ? 'blur(14px)' : 'none',
        transition: 'all 0.35s ease',
        borderBottom: scrolled ? '1px solid rgba(124,58,165,0.15)' : 'none',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: isMobile ? 56 : 68,
        }}>
          <div style={{ cursor: 'pointer' }} onClick={() => go('hero')}>
            <span style={{ fontFamily: FONTS.heading, fontSize: isMobile ? 20 : 24, fontWeight: 700 }}>
              <span style={{ color: BRAND.warm }}>Empower</span><span style={{ color: BRAND.teal }}>ED</span>
            </span>
          </div>

          {/* Desktop */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
              {LINKS.map(l => (
                <a key={l.id} onClick={() => go(l.id)} style={{
                  color: active === l.id ? BRAND.warm : 'rgba(255,255,255,0.65)',
                  textDecoration: 'none', fontSize: 13, fontFamily: FONTS.body,
                  letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
                  transition: 'color 0.2s',
                  borderBottom: active === l.id ? `2px solid ${BRAND.warm}` : '2px solid transparent',
                  paddingBottom: 4,
                }}
                  onMouseEnter={e => (e.currentTarget.style.color = BRAND.warm)}
                  onMouseLeave={e => { if (active !== l.id) e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                >{l.label}</a>
              ))}

              {/* Pay dropdown */}
              <div ref={payRef} style={{ position: 'relative' }}
                onMouseEnter={() => setPayOpen(true)} onMouseLeave={() => setPayOpen(false)}>
                <a onClick={() => setPayOpen(o => !o)}
                  aria-haspopup="menu" aria-expanded={payOpen}
                  style={{
                    color: payOpen ? BRAND.warm : 'rgba(255,255,255,0.65)',
                    textDecoration: 'none', fontSize: 13, fontFamily: FONTS.body,
                    letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
                    transition: 'color 0.2s', paddingBottom: 4,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                  Pay <span style={{ fontSize: 9, transform: payOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                </a>
                {payOpen && (
                  <div role="menu" style={{
                    position: 'absolute', top: 'calc(100% + 12px)', right: 0, minWidth: 240,
                    background: 'rgba(13,8,20,0.98)', backdropFilter: 'blur(14px)',
                    border: '1px solid rgba(124,58,165,0.25)', borderRadius: 10,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)', overflow: 'hidden', zIndex: 200,
                  }}>
                    <a href={VENMO_URL} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'block', padding: '14px 18px', textDecoration: 'none',
                        borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,165,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ color: BRAND.warm, fontFamily: FONTS.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Pay by Venmo</div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontFamily: FONTS.body, fontSize: 14 }}>@{VENMO_HANDLE}</div>
                    </a>
                    <a onClick={copyZelle}
                      style={{ display: 'block', padding: '14px 18px', textDecoration: 'none', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,165,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ color: BRAND.warm, fontFamily: FONTS.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Pay by Zelle</div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontFamily: FONTS.body, fontSize: 14 }}>{ZELLE_NUMBER}</div>
                      <div style={{ color: zelleCopied ? BRAND.teal : 'rgba(255,255,255,0.4)', fontFamily: FONTS.body, fontSize: 11, marginTop: 4 }}>
                        {zelleCopied ? '✓ Copied' : 'Click to copy'}
                      </div>
                    </a>
                  </div>
                )}
              </div>

              <button onClick={() => go('lead')} style={{
                background: BRAND.warm, color: BRAND.navy, border: 'none',
                padding: '10px 24px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                fontFamily: FONTS.body, cursor: 'pointer', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = BRAND.warmDark; e.currentTarget.style.color = BRAND.white; }}
                onMouseLeave={e => { e.currentTarget.style.background = BRAND.warm; e.currentTarget.style.color = BRAND.navy; }}
              >Get Started</button>
            </div>
          )}

          {/* Hamburger */}
          {isMobile && (
            <button onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', width: 44, height: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 8, position: 'relative', zIndex: 200 }}>
              <span style={{ display: 'block', width: 24, height: 2, background: BRAND.warm, borderRadius: 1, transition: 'all 0.3s ease', transform: menuOpen ? 'rotate(45deg) translate(3px, 3px)' : 'none' }} />
              <span style={{ display: 'block', width: 24, height: 2, background: BRAND.warm, borderRadius: 1, transition: 'all 0.3s ease', opacity: menuOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: 24, height: 2, background: BRAND.warm, borderRadius: 1, transition: 'all 0.3s ease', transform: menuOpen ? 'rotate(-45deg) translate(3px, -3px)' : 'none' }} />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile drawer */}
      {isMobile && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 140, background: 'rgba(0,0,0,0.6)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none', transition: 'opacity 0.3s ease' }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: '85%', maxWidth: 340, zIndex: 145,
            background: `linear-gradient(180deg, ${BRAND.navy} 0%, ${BRAND.navyLight} 100%)`,
            transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex', flexDirection: 'column',
            borderLeft: `1px solid ${BRAND.warm}20`,
            overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          }}>
            <div style={{ height: 64, flexShrink: 0 }} />

            <div style={{ flex: 1, padding: '8px 28px' }}>
              {LINKS.map(l => (
                <a key={l.id} onClick={() => go(l.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '20px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  color: active === l.id ? BRAND.warm : 'rgba(255,255,255,0.7)',
                  textDecoration: 'none', fontSize: 18, fontFamily: FONTS.body,
                  fontWeight: active === l.id ? 700 : 500, cursor: 'pointer',
                }}>
                  {active === l.id && <div style={{ width: 4, height: 22, borderRadius: 2, background: BRAND.warm, flexShrink: 0 }} />}
                  {l.label}
                </a>
              ))}

              {/* Pay (expandable) */}
              <div>
                <a onClick={() => setPayMobileOpen(o => !o)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                  padding: '20px 0',
                  color: payMobileOpen ? BRAND.warm : 'rgba(255,255,255,0.7)',
                  fontSize: 18, fontFamily: FONTS.body, fontWeight: 500, cursor: 'pointer',
                }}>
                  Pay
                  <span style={{ fontSize: 12, transform: payMobileOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                </a>
                {payMobileOpen && (
                  <div style={{ paddingLeft: 14, paddingBottom: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <a href={VENMO_URL} target="_blank" rel="noopener noreferrer"
                      style={{ textDecoration: 'none', padding: '12px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.04)' }}>
                      <div style={{ color: BRAND.warm, fontFamily: FONTS.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Pay by Venmo</div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontFamily: FONTS.body, fontSize: 15 }}>@{VENMO_HANDLE}</div>
                    </a>
                    <a onClick={copyZelle}
                      style={{ cursor: 'pointer', padding: '12px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.04)' }}>
                      <div style={{ color: BRAND.warm, fontFamily: FONTS.body, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Pay by Zelle</div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontFamily: FONTS.body, fontSize: 15 }}>{ZELLE_NUMBER}</div>
                      <div style={{ color: zelleCopied ? BRAND.teal : 'rgba(255,255,255,0.4)', fontFamily: FONTS.body, fontSize: 12, marginTop: 4 }}>
                        {zelleCopied ? '✓ Copied' : 'Tap to copy'}
                      </div>
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Strong CTAs */}
            <div style={{ padding: '20px 28px 44px', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => go('lead')} style={{
                background: BRAND.warm, color: BRAND.navy, border: 'none',
                padding: '18px 24px', borderRadius: 12, fontSize: 16, fontWeight: 700,
                fontFamily: FONTS.body, cursor: 'pointer', width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                boxShadow: '0 4px 16px rgba(124,58,165,0.3)',
              }}>📄 Get Free Resource Guide</button>

              <button onClick={() => go('assessments')} style={{
                background: 'transparent', color: BRAND.warm,
                border: `2px solid ${BRAND.warm}`,
                padding: '16px 24px', borderRadius: 12, fontSize: 15, fontWeight: 600,
                fontFamily: FONTS.body, cursor: 'pointer', width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}>📊 Take a Free Assessment</button>

              <a href="mailto:curtisempowered@gmail.com" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '14px', color: 'rgba(255,255,255,0.5)',
                fontFamily: FONTS.body, fontSize: 13, textDecoration: 'none',
              }}>✉️ curtisempowered@gmail.com</a>
            </div>
          </div>
        </>
      )}
    </>
  );
}
